import { useEffect, useRef } from 'react';
import { API_URL } from '../config/constants';
import logger from '../utils/logger';

export const REALTIME_EVENT_TYPES = Object.freeze({
  FINANCE_DATA_CHANGED: 'finance_data.changed',
  REVIEW_QUEUE_CHANGED: 'review_queue.changed',
  BANK_STATUS_CHANGED: 'bank_status.changed',
  NET_WORTH_CHANGED: 'net_worth.changed',
});

const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 15000;

const parseEventChunk = (chunk) => {
  const lines = chunk.split(/\r?\n/);
  let event = 'message';
  const dataLines = [];

  for (const line of lines) {
    if (!line || line.startsWith(':')) continue;
    if (line.startsWith('event:')) {
      event = line.slice(6).trim() || 'message';
      continue;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  if (dataLines.length === 0) return null;

  try {
    const payload = JSON.parse(dataLines.join('\n'));
    return {
      event,
      payload,
    };
  } catch (_error) {
    return null;
  }
};

const findSseBoundary = (buffer) => {
  const lfBoundaryIndex = buffer.indexOf('\n\n');
  const crlfBoundaryIndex = buffer.indexOf('\r\n\r\n');

  if (lfBoundaryIndex === -1) {
    return crlfBoundaryIndex === -1
      ? { index: -1, length: 0 }
      : { index: crlfBoundaryIndex, length: 4 };
  }

  if (crlfBoundaryIndex === -1 || lfBoundaryIndex < crlfBoundaryIndex) {
    return { index: lfBoundaryIndex, length: 2 };
  }

  return { index: crlfBoundaryIndex, length: 4 };
};

const consumeSseBuffer = (buffer, onMessage) => {
  let nextBuffer = buffer;
  let boundary = findSseBoundary(nextBuffer);

  while (boundary.index >= 0) {
    const chunk = nextBuffer.slice(0, boundary.index).trim();
    nextBuffer = nextBuffer.slice(boundary.index + boundary.length);

    if (chunk) {
      const parsed = parseEventChunk(chunk);
      if (parsed?.payload) {
        onMessage(parsed.payload, parsed.event);
      }
    }

    boundary = findSseBoundary(nextBuffer);
  }

  return nextBuffer;
};

const readRealtimeStream = async ({ response, onMessage, signal }) => {
  if (!response.body) {
    throw new Error('Realtime stream unavailable');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (!signal.aborted) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      buffer = consumeSseBuffer(buffer, onMessage);
    }
  } finally {
    reader.releaseLock();
  }
};

const useRealtimeEventStream = ({
  authenticatedFetch,
  householdId,
  enabled = true,
  onEvent,
}) => {
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!enabled || !authenticatedFetch || !householdId) return undefined;

    let cancelled = false;
    let reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;
    let reconnectTimer = null;
    let activeAbortController = null;

    const scheduleReconnect = () => {
      if (cancelled) return;
      reconnectTimer = window.setTimeout(() => {
        reconnectDelayMs = Math.min(reconnectDelayMs * 2, MAX_RECONNECT_DELAY_MS);
        void openStream();
      }, reconnectDelayMs);
    };

    const openStream = async () => {
      if (cancelled) return;

      activeAbortController = new AbortController();

      try {
        const response = await authenticatedFetch(`${API_URL}/realtime/stream`, {
          headers: {
            Accept: 'text/event-stream',
          },
          signal: activeAbortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Realtime stream failed (${response.status})`);
        }

        reconnectDelayMs = INITIAL_RECONNECT_DELAY_MS;

        await readRealtimeStream({
          response,
          signal: activeAbortController.signal,
          onMessage: (payload, event) => {
            if (payload?.householdId && String(payload.householdId) !== String(householdId)) {
              return;
            }
            onEventRef.current?.({
              ...payload,
              eventName: event,
            });
          },
        });
      } catch (error) {
        if (cancelled || activeAbortController.signal.aborted) {
          return;
        }
        if (process.env.NODE_ENV === 'development') {
          logger.warn('Realtime stream disconnected', error);
        }
      }

      scheduleReconnect();
    };

    void openStream();

    return () => {
      cancelled = true;
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
      }
      activeAbortController?.abort();
    };
  }, [authenticatedFetch, enabled, householdId]);
};

export default useRealtimeEventStream;
